import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import TrustBar from '../components/TrustBar'
import PartnersSection from '../components/PartnersSection'
import CMSGallery from '../components/CMSGallery'
import VehicleCard from '../components/VehicleCard'
import { fetchVehicles, getResizedImageUrl } from '../lib/cms'

export default function Home() {
  const [vehicles, setVehicles] = useState([])

  useEffect(() => {
    let isMounted = true
    async function load() {
      const data = await fetchVehicles()
      if (isMounted) setVehicles(data)
    }
    load()
    return () => { isMounted = false }
  }, [])

  // Uploaded real asset photo of fleet with Transport4U branding
  const heroImage = 'https://assets.auxodev.dk/assets/070d40dc-3ad7-4f6e-9a43-382bc6e79eb3/photo-2026-09-06-15-33-19.jpg'

  return (
    <div className="space-y-0">
      <SEO
        title="Pålidelig godstransport og temperaturstyret logistik | Transport4U"
        description="Transport4U leverer fleksible og temperaturstyrede transportløsninger (-20°C til +25°C) til erhvervskunder. Døgnåben 24/7 bestilling, op til 8 europaller og afgang inden for 2 timer."
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-navy-950 via-navy-900 to-navy-950 text-white overflow-hidden pt-12 pb-20 lg:pt-16 lg:pb-28">
        {/* Subtle background logistics accent lines */}
        <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:24px_24px]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-stretch">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left flex flex-col justify-center items-center lg:items-start">
              {/* Badge */}
              <div className="w-fit inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-sky-950 border border-sky-500/40 text-sky-300 text-xs font-semibold">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Over 2 års erfaring med erhvervslogistik</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-black text-white tracking-tight leading-[1.15]">
                Pålidelig godstransport og temperaturstyret logistik
              </h1>

              {/* Subtitle / Intro */}
              <p className="text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
                Transport4U leverer fleksible og temperaturstyrede transportløsninger til alle typer erhvervskunder. Med topmoderne køretøjer, præcis temperaturkontrol fra -20°C til +25°C og erfarne chauffører garanterer vi højeste leveringssikkerhed.
              </p>

              {/* Key Bullet Highlights */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2 text-xs font-semibold text-slate-200 text-left">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-sky-400 shrink-0" aria-hidden="true" />
                  <span>24/7 Døgnåben bestilling</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-sky-400 shrink-0" aria-hidden="true" />
                  <span>Afgang inden for 2 timer</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-sky-400 shrink-0" aria-hidden="true" />
                  <span>Op til 8 europaller</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-sky-400 shrink-0" aria-hidden="true" />
                  <span>Transport fra 1 kg</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-sky-400 shrink-0" aria-hidden="true" />
                  <span>Ubrudt kølekæde</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-sky-400 shrink-0" aria-hidden="true" />
                  <span>Fuld godsforsikring</span>
                </div>
              </div>

              {/* CTA Buttons & Phone */}
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <Link
                  to="/kontakt"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-sky-500 hover:bg-sky-400 active:bg-sky-600 text-slate-950 font-extrabold text-sm sm:text-base transition-colors shadow-lg shadow-sky-500/25 cursor-pointer"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <Link
                  to="/vores-biler"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-slate-800/90 hover:bg-slate-750 text-white font-bold text-sm sm:text-base border border-slate-700 transition-colors"
                >
                  <span>Se vores transportbiler</span>
                  <i className="fa-solid fa-truck-front text-sky-400 text-sm" aria-hidden="true" />
                </Link>
              </div>

              {/* Prominent Phone Call Action */}
              <div className="pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-3 text-xs">
                <span className="text-slate-400">Kontakt og bestilling:</span>
                <a
                  href="mailto:transport4u.dk@gmail.com"
                  className="inline-flex items-center gap-1.5 text-sky-400 hover:text-sky-300 font-semibold whitespace-nowrap transition-colors"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>transport4u.dk@gmail.com</span>
                </a>
                <span className="text-slate-600">|</span>
                <a
                  href="tel:+4550352035"
                  className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white font-medium whitespace-nowrap transition-colors"
                  aria-label="Ring til 50 35 20 35"
                >
                  <i className="fa-solid fa-phone text-xs text-sky-400" aria-hidden="true" />
                  <span>Tlf: 50 35 20 35</span>
                </a>
              </div>
            </div>

            {/* Right Hero Image Card */}
            <div className="lg:col-span-5 flex">
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/80 shadow-2xl bg-slate-900 w-full h-full">
                <img
                  src={getResizedImageUrl(heroImage, { width: 900, quality: 85 })}
                  alt="Transport4U moderne vognpark med Mercedes-Benz kølebiler og kassevogne"
                  className="w-full h-full min-h-[360px] sm:min-h-[440px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & USP Bar */}
      <TrustBar />

      {/* Hvorfor vælge os? Section */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-3 mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-600">
              Kvalitet & Pålidelighed
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Hvorfor vælge Transport4U?
            </h2>
            <p className="text-slate-600 text-base leading-relaxed">
              Vi kombinerer nye biler, avanceret temperaturstyring og dedikerede chauffører for at levere en fejlfri logistikoplevelse for din virksomhed.
            </p>
          </div>

          {/* Non-clickable USP Grid - strictly no hover effects */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* 1. Biler af høj kvalitet og ny klasse */}
            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-200/80">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600 mb-5">
                <i className="fa-solid fa-truck-front text-xl" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                Biler af høj kvalitet og ny klasse
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Vognparken består af topmoderne køretøjer med avancerede klimaanlæg og høj driftssikkerhed på vejene.
              </p>
            </div>

            {/* 2. Præcis temperaturstyring */}
            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-200/80">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600 mb-5">
                <i className="fa-solid fa-temperature-half text-xl" aria-hidden="true" />
              </div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                  Præcis temperaturstyring
                </h3>
              </div>
              <div className="mt-1">
                <span className="inline-block px-2.5 py-0.5 rounded-md bg-sky-100 text-sky-800 text-xs font-extrabold">
                  -20°C til +25°C
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Uanset om det gælder dybfrost eller kontrolleret varme, holder Transport4U den præcise temperatur under hele turen.
              </p>
            </div>

            {/* 3. Erfarne chauffører */}
            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-200/80">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600 mb-5">
                <i className="fa-solid fa-id-card-clip text-xl" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                Erfarne chauffører
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Professionelle og ansvarsfulde chauffører sikrer, at godset behandles med størst mulig omhu og leveres til tiden.
              </p>
            </div>

            {/* 4. Godsforsikring */}
            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-200/80">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600 mb-5">
                <i className="fa-solid fa-shield-halved text-xl" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                Fuld godsforsikring
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Varerne er dækket af godsforsikring fra start til slut, så du trygt kan overlade forsendelserne til os.
              </p>
            </div>

            {/* 5. Transport fra 1 kg & ikke-standard gods */}
            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-200/80">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600 mb-5">
                <i className="fa-solid fa-box text-xl" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                Transport fra 1 kg
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Transport4U håndterer både mindre pakker og ikke-standard gods. Ved ikke-standard gods bedes dette angivet ved bestilling.
              </p>
            </div>

            {/* 6. Kapacitet op til 8 pallepladser */}
            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-200/80">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600 mb-5">
                <i className="fa-solid fa-pallet text-xl" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">
                Op til 8 europaller
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Køretøjerne kan håndtere op til 8 europaller pr. bil. Praktisk placering i Silkeborg og fleksibel prispolitik tilpasset dit behov.
              </p>
            </div>
          </div>

          {/* Conversion CTA banner after USP */}
          <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-sky-50 border border-sky-200/80 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="space-y-1 text-center sm:text-left">
              <h4 className="text-lg font-bold text-sky-950">
                Har du brug for en fleksibel pris eller fast ruteaftale?
              </h4>
              <p className="text-sm text-slate-600">
                Vi tilpasser priserne til jeres konkrete behov og volumen.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-sm transition-colors shadow-sm"
              >
                <span>Kontakt os for pris</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fleksibel bestilling og ekspreslevering 24/7 Section */}
      <section className="py-20 bg-navy-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-950 border border-sky-500/40 text-sky-300 text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-bolt text-sky-400" aria-hidden="true" />
                <span>Ekspres & Akutservice</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                Fleksibel bestilling og ekspreslevering 24/7
              </h2>

              <p className="text-slate-300 text-base leading-relaxed">
                Logistik sover aldrig. Derfor står Transport4U klar døgnet rundt – også i weekender og på helligdage. Vi tilbyder hurtige afgange og skræddersyet kørsel, når det haster mest.
              </p>

              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-500/20 text-sky-400 border border-sky-500/30">
                    <i className="fa-solid fa-headset text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white">Modtagelse af ordrer 24/7</h4>
                    <p className="text-sm text-slate-300">
                      Transport4U står klar døgnet rundt og arbejder også i weekender og på helligdage.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-500/20 text-sky-400 border border-sky-500/30">
                    <i className="fa-solid fa-calendar-day text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white">Bestil fra dag-til-dag eller dag-til-i-dag</h4>
                    <p className="text-sm text-slate-300">
                      Ved akut behov tilbydes hurtig udrykning med mulighed for afgang på ruten inden for 2 timer.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-500/20 text-sky-400 border border-sky-500/30">
                    <i className="fa-solid fa-clock-check text-base" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white">Rettidighed og leveringsgaranti</h4>
                    <p className="text-sm text-slate-300">
                      Fokus på professionel logistik og en tidsplan, som din virksomhed altid kan regne med.
                    </p>
                  </div>
                </div>
              </div>

              {/* Direct CTA */}
              <div className="pt-4 flex flex-col sm:flex-row items-center gap-4">
                <Link
                  to="/kontakt"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-extrabold text-sm transition-colors shadow-lg shadow-sky-500/20 whitespace-nowrap"
                >
                  <span>Bestil transport online</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <a
                  href="mailto:transport4u.dk@gmail.com"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-sm border border-slate-700 transition-colors"
                >
                  <i className="fa-solid fa-envelope text-sky-400 text-xs" aria-hidden="true" />
                  <span>Send e-mail</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-slate-800/80 rounded-2xl p-6 sm:p-8 border border-slate-700 text-center space-y-6">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-sky-500/20 text-sky-400 ring-4 ring-sky-500/10">
                  <i className="fa-solid fa-stopwatch text-2xl" aria-hidden="true" />
                </div>
                <div className="space-y-2">
                  <span className="text-xs uppercase tracking-widest text-sky-400 font-bold">
                    Akut udrykning
                  </span>
                  <h3 className="text-2xl font-black text-white">
                    Afgang inden for 2 timer
                  </h3>
                  <p className="text-sm text-slate-300">
                    Står du med en hastesag? Ring straks – vi starter bilen og kører mod afhentningsstedet hurtigst muligt.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-900 border border-slate-700 text-center">
                  <span className="text-xs text-slate-400 block">Døgnvagt telefon:</span>
                  <a
                    href="tel:+4550352035"
                    className="text-2xl font-black text-white hover:text-sky-400 transition-colors block mt-1 whitespace-nowrap"
                  >
                    50 35 20 35
                  </a>
                  <a
                    href="mailto:transport4u.dk@gmail.com"
                    className="text-xs text-sky-400 hover:underline block mt-1"
                  >
                    transport4u.dk@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vores biler / Transportflåde Teaser (Iceotemp reference) */}
      <section className="py-20 bg-slate-50 border-t border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
                Moderne vognpark
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                Vores køretøjer og kapacitet
              </h2>
              <p className="text-slate-600 text-sm sm:text-base max-w-xl">
                Overskuelig præsentation af vores køretøjer. Udstyret med avancerede klimaanlæg, høj lasteevne og godsforsikring.
              </p>
            </div>

            <Link
              to="/vores-biler"
              className="inline-flex items-center gap-2 text-sm font-bold text-sky-600 hover:text-sky-700 whitespace-nowrap"
            >
              <span>Se alle køretøjer og specifikationer</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Vehicle cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((vehicle) => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>

          {/* Temperature Transport Teaser Callout */}
          <div className="mt-12 p-8 rounded-2xl bg-white border border-slate-200/90 shadow-sm flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 border border-sky-100">
                <i className="fa-solid fa-temperature-arrow-down text-2xl" aria-hidden="true" />
              </div>
              <div className="space-y-1">
                <h4 className="text-lg font-bold text-slate-900">
                  Behov for temperaturstyret kørsel fra -20°C til +25°C?
                </h4>
                <p className="text-sm text-slate-600">
                  Vi varetager dybfrost, kølevarer og kontrolleret varme for fødevareproducenter, catering og medicinalleverandører.
                </p>
              </div>
            </div>

            <Link
              to="/temperaturstyret-transport"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-colors shrink-0"
            >
              <span>Læs om temperaturstyret transport</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Partners and Clients */}
      <PartnersSection />

      {/* CMS Gallery Teaser */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-sky-600">
              Fotogalleri
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Billeder fra Transport4U's vognpark
            </h2>
            <p className="text-slate-600 text-sm">
              Klik på et billede for at se køretøjerne i stor visning.
            </p>
          </div>

          <CMSGallery gallerySlug="transport4u" maxItems={6} />
        </div>
      </section>

      {/* Final Pre-footer Conversion Section */}
      <section className="py-16 bg-slate-100 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Klar til at sende dit gods afsted med Transport4U?
          </h2>
          <p className="text-slate-600 text-base max-w-2xl mx-auto">
            Hvad enten det er 1 kg eller 8 europaller, akut udrykning inden for 2 timer eller fast ruteaftale, står vi klar døgnet rundt.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-base transition-colors shadow-md"
            >
              <span>Udfyld bestillingsformular</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>

            <a
              href="mailto:transport4u.dk@gmail.com"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-xl bg-navy-900 hover:bg-navy-850 text-white font-bold text-base transition-colors shadow-md whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-sky-400" aria-hidden="true" />
              <span>Send e-mail</span>
            </a>

            <a
              href="tel:+4550352035"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold text-sm transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-sky-600" aria-hidden="true" />
              <span>Tlf: 50 35 20 35</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
